import {
    TrackableEvent,
    TrackableEventWrapper,
    TrackableEventType,
    UserSignedInEvent,
    UserSignedOutEvent,
    UserUpgradedEvent,
    ExpressedIntegrationInterestEvent,
    SuggestedIntegrationEvent,
    ErrorEncounteredEvent,
    User,
    UpgradeDialogOpenedEvent,
    ExpressedRoadmapFeatureInterestEvent,
    SetupGuideOpenedEvent,
    SetupGuideStepCompletedEvent,
    ChatOpenedEvent,
    ChatQuestionAskedEvent,
    ChatRefusedEvent,
    ChatAllowanceActionClickedEvent,
    ChatAnswerRatedEvent,
    AuditResultOpenedEvent,
    AuditListDownloadedEvent,
} from "@/types/generated/management-api-types";
type Organization = User["currentOrganization"];

type EventDiscriminator = {
    eventType: TrackableEventType;
    type: TrackableEventType;
};

type EventProperties<T> = Omit<T, keyof EventDiscriminator | "organization">;

class TrackableEventFactory {
    public static createUserSignedInEvent(
        properties: EventProperties<UserSignedInEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "User Signed In",
            properties,
            organization,
        );
    }

    public static createUserSignedOutEvent(
        properties: EventProperties<UserSignedOutEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "User Signed Out",
            properties,
            organization,
        );
    }

    public static createUserUpgradedEvent(
        properties: EventProperties<UserUpgradedEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "User Upgraded",
            properties,
            organization,
        );
    }

    public static createExpressedIntegrationInterestEvent(
        properties: EventProperties<ExpressedIntegrationInterestEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "Expressed Integration Interest",
            properties,
            organization,
        );
    }

    public static createSuggestedIntegrationEvent(
        properties: EventProperties<SuggestedIntegrationEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "Suggested Integration",
            properties,
            organization,
        );
    }

    public static createExpressedRoadmapFeatureInterest(
        properties: EventProperties<ExpressedRoadmapFeatureInterestEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "Expressed Roadmap Feature Interest",
            properties,
            organization,
        );
    }

    public static createUpgradeDialogOpenedEvent(
        properties: EventProperties<UpgradeDialogOpenedEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "Upgrade Dialog Opened",
            properties,
            organization,
        );
    }

    public static createSetupGuideOpenedEvent(
        properties: EventProperties<SetupGuideOpenedEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "Setup Guide Opened",
            properties,
            organization,
        );
    }

    public static createSetupGuideStepCompletedEvent(
        properties: EventProperties<SetupGuideStepCompletedEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "Setup Guide Step Completed",
            properties,
            organization,
        );
    }

    public static createChatOpenedEvent(
        properties: EventProperties<ChatOpenedEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "Chat Opened",
            properties,
            organization,
        );
    }

    public static createChatQuestionAskedEvent(
        properties: EventProperties<ChatQuestionAskedEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "Chat Question Asked",
            properties,
            organization,
        );
    }

    public static createChatRefusedEvent(
        properties: EventProperties<ChatRefusedEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "Chat Refused",
            properties,
            organization,
        );
    }

    public static createChatAllowanceActionClickedEvent(
        properties: EventProperties<ChatAllowanceActionClickedEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "Chat Allowance Action Clicked",
            properties,
            organization,
        );
    }

    public static createChatAnswerRatedEvent(
        properties: EventProperties<ChatAnswerRatedEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "Chat Answer Rated",
            properties,
            organization,
        );
    }

    /**
     * Since this event can be thrown from the top level ErrorBoundary
     * we do not always have access to the organization
     */
    public static createErrorEncounteredEvent(
        properties: EventProperties<ErrorEncounteredEvent>,
        organization?: Organization,
    ) {
        return {
            event: "Error Encountered",
            properties: properties as unknown as TrackableEvent,
            context: {
                groupId: organization?.id,
            },
        };
    }

    public static createAuditResultOpenedEvent(
        properties: EventProperties<AuditResultOpenedEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "Audit Result Opened",
            properties,
            organization,
        );
    }

    public static createAuditListDownloadedEvent(
        properties: EventProperties<AuditListDownloadedEvent>,
        organization: Organization,
    ): TrackableEventWrapper {
        return TrackableEventFactory.create(
            "Audit List Downloaded",
            properties,
            organization,
        );
    }

    private static create(
        event: TrackableEventType,
        properties: Record<string, unknown>,
        organization: Organization,
    ): TrackableEventWrapper {
        return {
            event,
            properties: properties as unknown as TrackableEvent,
            context: {
                groupId: organization.id,
            },
        };
    }
}

export default TrackableEventFactory;
